/*
 *   File : signup.js
 *   Author : https://evoqins.com
 *   Description : Sign up page
 *   Integrations : js-cookie
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

//Custom components
import { CustomPhoneInput, CustomTextInput, OTPInput } from '../../Component/FormElements';
import { PrimaryButton } from '../../Component/Buttons';
import { CustomCheckBox } from '../../Component/Others';
import { Icon } from '../../Component/Icon';
import { APIService } from '../../Service';
import { _scrollToTop } from '../../Helper';

//Style
import style from "../../Styles/Component/auth.module.scss";

// Assets
import SignupBanner from "../../Assets/Images/Auth/login-step.webp";
import OTPBanner from "../../Assets/Images/Auth/otp-step.webp";


const SignUp = (props) => {

    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState(1);
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [number, setNumber] = useState(location.state && location.state.mobile ? location.state.mobile : "");
    const [numberError, setNumberError] = useState("");
    const [validityCheck, setValidityCheck] = useState(true);

    // Otp 
    const [otpValue, setOtpValue] = useState("");
    const [otpValueError, setOtpValueError] = useState("");
    const [focusedInput, setFocusedInput] = useState(2);

    const [loading, setLoading] = useState(false);
    const [disableResend, setDisabledResend] = useState(false);
    const [otpTimer, setOTPTimer] = useState(30);

    useEffect(() => {
        if (otpTimer !== 0 && step === 2) {
            setTimeout(() => {
                setOTPTimer(timer => timer - 1);
            }, 1000);
        }
    }, [otpTimer, step]);

    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();
    }, [step]);

    useEffect(() => {
        setNameError("");
    }, [name]);

    useEffect(() => {
        setEmailError("");
    }, [email]);

    useEffect(() => {
        setNumberError("");
    }, [number]);

    useEffect(() => {
        setOtpValueError("");
    }, [otpValue]);

    // handle check validity
    const _handleValidityCheck = () => {
        setValidityCheck(!validityCheck);
    };

    // handle navigate login 
    const _handleLogin = () => {
        navigate("/");
    };

    //handle Otp change
    const _handleChangeOTP = (value) => {
        setOtpValue(value);
    };

    // handle resend OTP 
    const _handleResendOtp = () => {
        setDisabledResend(true);
        _getSignUpOTP("resend");
    };

    // handle back step 
    const _handleBack = () => {
        setOtpValue("");
        setOtpValueError("");
        setFocusedInput(2);
        setOTPTimer(30);
    };

    // handle navigate
    const _handleNavigate = () => {
        navigate("/terms-conditions");
        props.handlePublicRouter();
    };


    // handle get otp 
    const _handleGetOtp = () => {
        let is_valid = true;
        let validate_email = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
        let validate_number = /^[6-9]\d{9}$/;

        if (name.trim().length === 0) {
            setNameError("Enter your full name");
            is_valid = false;
        }

        if (email.trim().length === 0) {
            setEmailError("Enter your email");
            is_valid = false;
        } else if (!validate_email.test(email)) {
            setEmailError("Enter a valid email address");
            is_valid = false;
        }

        if (number.trim().length === 0) {
            setNumberError("Enter your number");
            is_valid = false;
        } else if (!validate_number.test(number)) {
            setNumberError("Enter a valid phone number");
            is_valid = false;
        }

        if (is_valid === true) {
            setLoading(true);
            // API call
            _getSignUpOTP();

        }
    };

    // handle verify OTP 
    const _handleVerifyOtp = () => {
        if (otpValue.length < 4) {
            setOtpValueError("Please provide received otp");
            setFocusedInput(2);
        } else {
            // API Call
            setLoading(true);
            _verifyOTP();
        }
    };


    // ===================================== //
    // ============ API CALLS ============== //

    // Send Signup OTP to mobile
    const _getSignUpOTP = async (type) => {
        let url = "auth/signup";
        let data = {
            "name": name,
            "email": email,
            "mobile": number,
            "source": "WEB",
            "referal_broker_id": null,
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            if (type == "resend") {
                toast.dismiss();
                toast.success('OTP re-sent successfully');
            }
            setOtpValue("");
            setFocusedInput(2);
            setOTPTimer(30);
            setStep(2);
        } else {
            toast.dismiss();
            toast.error(response.message);
        }

        setLoading(false);
        setDisabledResend(false);
    };

    // Verify Signup OTP
    const _verifyOTP = async () => {
        let url = "auth/validate-signup-otp";
        let data = {
            "name": name,
            "email": email,
            "mobile": number,
            "otp": otpValue,
            "source": "WEB",
        };

        const response = await APIService(url, data);

        if (response.status_code === 200) {
            navigate('/login');
            toast.dismiss();
            toast.success("Account created successfully, login to continue");
        } else {
            toast.dismiss();
            toast.error(response.message);
        }
        setLoading(false);
    };

    return (
        <section className='min-vh-100 mb-5 pb-5'>
            <div className='container'>
                <div className='row pt-3'>
                    <div className='offset-lg-1 col-lg-10 col-12'>
                        <div className={`${style.e_auth_wrapper} border-radius-16 overflow-hidden`}>
                            <div className='row'>
                                <div className='col-lg-6 col-md-6 col-sm-12 col-12 pe-0 d-lg-block d-md-block d-sm-none d-none'>
                                    <div className='e-bg-lotion-dark e-border-1px-right-lavender-light h-100'>
                                        <img src={step === 1 ? SignupBanner : OTPBanner}
                                            draggable={false}
                                            className='w-100'
                                            alt='signup' />
                                    </div>
                                </div>

                                <div className='col-lg-6 col-md-6 col-sm-12 col-12 ps-lg-0 ps-md-0'>
                                    {/* Phone input section */}
                                    {step === 1 ?
                                        <div className='padding-32px-all'>
                                            <div className='text-center mb-3'>
                                                <h3 className='mb-0 e-primary-font-clash-display e-text-primary-color e-font-18 e-font-weight-500 line-height-24px'>
                                                    Create an Account
                                                </h3>
                                                <p className='mb-0 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                                    Please enter your details to create a new account
                                                </p>
                                            </div>

                                            {/* user name */}
                                            <CustomTextInput label="Full name"
                                                placeholder="Enter your full name"
                                                type="text"
                                                require
                                                autoFocus={true}
                                                value={name}
                                                error={nameError}
                                                className="w-100"
                                                onKeyPress={_handleGetOtp}
                                                handleChange={(e) => setName(e)} />

                                            {/* user email */}
                                            <CustomTextInput label="Email"
                                                placeholder="Enter your email address"
                                                type="text"
                                                require
                                                value={email}
                                                error={emailError}
                                                wrapperClass="py-3"
                                                className="w-100"
                                                onKeyPress={_handleGetOtp}
                                                handleChange={(e) => setEmail(e)} />

                                            {/* Phone number */}
                                            <CustomPhoneInput label="Phone number"
                                                placeholder="Enter your phone number"
                                                require
                                                value={number}
                                                error={numberError}
                                                className="w-100"
                                                onKeyPress={_handleGetOtp}
                                                handleChange={(e) => {
                                                    if (e.length <= 10) {
                                                        setNumber(e);
                                                    }
                                                }} />

                                            {/* Continue button */}
                                            <PrimaryButton name="Get OTP"
                                                status={loading}
                                                disabled={loading}
                                                className="mt-4 w-100 h-48"
                                                onPress={_handleGetOtp} />

                                            <p className='mb-3 padding-12px-top e-font-12 e-alt-font-lato line-height-18px e-text-sonic-silver e-font-weight-400'>
                                                By continuing, you agree to our
                                                <span className='ps-1 e-text-primary-color cursor-pointer text-decoration-hover-underline'
                                                    onClick={_handleNavigate}>
                                                    Terms and Conditions
                                                </span>
                                            </p>

                                            <p className='mb-0 e-font-14 e-alt-font-lato line-height-20px e-text-chinese-black e-font-weight-400'>
                                                Already have an account?
                                                <span className='ps-1 e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline'
                                                    onClick={_handleLogin}>
                                                    Log in
                                                </span>
                                            </p>

                                            {/* Custom check box */}
                                            <CustomCheckBox label="Keep me informed about investment opportunities, SIP plans, and other updates on WhatsApp."
                                                className="pt-3"
                                                checked={validityCheck}
                                                handleCheck={_handleValidityCheck} />
                                        </div>
                                        :
                                        // OTP section
                                        <div className='padding-32px-lr py-4'>
                                            <Icon icon={"back-icon"}
                                                size="28px"
                                                className='cursor-pointer'
                                                onClick={_handleBack} />

                                            <div className='text-center pb-3 pt-4'>
                                                <h3 className='mb-0 e-primary-font-clash-display e-text-primary-color e-font-20 e-font-weight-500 line-height-24px'>
                                                    Verify OTP
                                                </h3>
                                                <p className='px-lg-5 px-3 mb-0 e-alt-font-lato e-text-outer-space e-font-14 e-font-weight-400 line-height-20px'>
                                                    Enter the One-Time Password (OTP) sent to your registered mobile number <span className='d-inline-flex'>+91-{number.replace(/(\d{2})\d{5}(\d{3})/, '$1*****$2')}</span>
                                                </p>
                                            </div>

                                            {/* Enter OTP input */}
                                            <OTPInput label="Enter OTP"
                                                require
                                                ref={inputRef}
                                                value={otpValue}
                                                spellCheck="false"
                                                error={otpValueError}
                                                focused_input={focusedInput}
                                                onFocus={() => setFocusedInput(2)}
                                                onBlur={() => setFocusedInput(0)}
                                                onKeyPress={_handleVerifyOtp}
                                                onChange={_handleChangeOTP} />

                                            <p className='d-flex gap-1 flex-wrap mb-3 mt-4 e-font-14 e-alt-font-lato line-height-20px e-text-chinese-black e-font-weight-400'>
                                                Didn’t receive code yet?
                                                <span className={`${disableResend || otpTimer !== 0 ? "pe-none opacity-75" : ""} text-nowrap e-text-primary-color e-font-weight-600 cursor-pointer text-decoration-hover-underline`}
                                                    onClick={_handleResendOtp}>
                                                    Resend OTP
                                                    {
                                                        otpTimer !== 0 && ` in ${otpTimer} ${otpTimer > 1 ? "seconds" : "second"}`
                                                    }
                                                </span>
                                            </p>

                                            {/* Verify otp */}
                                            <PrimaryButton name="Continue"
                                                className="w-100 h-48"
                                                status={loading}
                                                disabled={loading}
                                                onPress={_handleVerifyOtp} />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SignUp;
